import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SellerVerification = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();


  // --------------------------------------
  // STEP 1 — ACCOUNT INFO
  // --------------------------------------
  const [account, setAccount] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    businessType: "",
  });

  // --------------------------------------
  // STEP 2 — BUSINESS DETAILS
  // --------------------------------------
  const [business, setBusiness] = useState({
    businessName: "",
    businessAddress: "",
    city: "",
    state: "",
    pincode: "",
    website: "",
    yearEstablished: "",
    employeeCount: "",
    profileImage: "",
  });

  // --------------------------------------
  // STEP 3 — PRODUCT DETAILS
  // --------------------------------------
  const [product, setProduct] = useState({
    categories: "",
    subCategories: "",
    description: "",
  });

  // --------------------------------------
  // STEP 4 — GST + DOCUMENTS
  // --------------------------------------
  const [gst, setGst] = useState({
    gstNumber: "",
    panNumber: "",
    aadharURL: "",
    panurl: "",
    gstDocURL: "",
  });

  const [errors, setErrors] = useState({});

  // Generic input handler
  const handleChange = (e, setter, state) => {
    setter({ ...state, [e.target.name]: e.target.value });
  };

  // File name extract
  const handleFile = (e, setter, state, key) => {
    const file = e.target.files[0];
    if (file) {
      setter({ ...state, [key]: file.name });
    }
  };

  // Validation
  const validateStep = () => {
    const err = {};

    if (step === 1) {
      if (!account.fullName.trim()) err.fullName = "Full name is required";
      if (!/^\d{10}$/.test(account.phone)) err.phone = "10-digit mobile required";
      if (!account.email.includes("@")) err.email = "Invalid email";
      if (account.password.length < 6)
        err.password = "Password must be 6+ characters";
      if (account.password !== account.confirmPassword)
        err.confirmPassword = "Passwords do not match";
      if (!account.businessType) err.businessType = "Select business type";
    }

    if (step === 2) {
      if (!business.businessName.trim())
        err.businessName = "Business name required";
      if (!business.city.trim()) err.city = "City required";
      if (!business.state.trim()) err.state = "State required";
      if (!/^\d{6}$/.test(business.pincode))
        err.pincode = "Valid pincode required";
    }

    if (step === 3) {
      if (!product.categories) err.categories = "Select category";
      if (!product.subCategories) err.subCategories = "Select sub-category";
      if (!product.description.trim())
        err.description = "Description required";
    }

    if (step === 4) {
      if (!gst.gstNumber.trim()) err.gstNumber = "GST number required";
      if (!gst.panNumber.trim()) err.panNumber = "PAN number required";
      if (!gst.aadharURL) err.aadharURL = "Upload Aadhaar";
      if (!gst.panurl) err.panurl = "Upload PAN";
      if (!gst.gstDocURL) err.gstDocURL = "Upload GST document";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleNext = () => validateStep() && setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  // --------------------------------------
  // FINAL SUBMIT → API POST
  // --------------------------------------
  const handleSubmit = async () => {
    if (!validateStep()) return;

    const payload = {
      userId: 0,
      fullName: account.fullName,
      email: account.email,
      phone: account.phone,
      password: account.password,
      type: "Vendor", // FIXED
      createdAt: new Date().toISOString(),
      status: "Pending",
      verified: false,
      businessName: business.businessName,
      businessType: account.businessType,
      yearEstablished: business.yearEstablished,
      employeeCount: business.employeeCount,
      website: business.website,
      gstNumber: gst.gstNumber,
      panNumber: gst.panNumber,
      categories: product.categories,
      subCategories: product.subCategories,
      description: product.description,
      aadharURL: gst.aadharURL,
      panurl: gst.panurl,
      gstDocURL: gst.gstDocURL,
      profileImage: business.profileImage,
    };

    try {
      const res = await axios.post(
        "https://localhost:44370/api/Profile/register",
        payload
      );
      alert("Seller Registered Successfully!");
      console.log("Response:", res.data);
      navigate("____seller_dashboard");

    } catch (error) {
      console.error(error);
      alert("Error in registration!");
    }
  };

  return (
    <div className="w-full h-full p-6 flex flex-col md:flex-row gap-6 overflow-auto">

      {/* LEFT FORM */}
      <div className="flex-1 bg-white shadow-xl p-8 rounded-2xl">
        <h2 className="text-3xl font-bold text-center mb-8">Seller Verification</h2>

        {/* STEPS */}
        <div className="flex justify-between mb-8">
          {["Account", "Business", "Product", "Documents"].map((t, i) => (
            <div
              key={i}
              className={`flex-1 p-3 text-center rounded-md text-sm font-medium ${
                step === i + 1
                  ? "bg-blue-600 text-white"
                  : step > i + 1
                  ? "bg-green-200 text-green-800"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {t}
            </div>
          ))}
        </div>

        {/* STEP CONTENT */}
        <AnimatePresence mode="wait">
          {/* -------------------------------------- */}
          {/* STEP 1 — ACCOUNT INFO */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -80 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              {[
                { label: "Full Name", name: "fullName" },
                { label: "Phone", name: "phone" },
                { label: "Email", name: "email" },
                { label: "Password", name: "password", type: "password" },
                {
                  label: "Confirm Password",
                  name: "confirmPassword",
                  type: "password",
                },
              ].map((field) => (
                <div key={field.name}>
                  <label className="font-medium">{field.label}</label>
                  <input
                    type={field.type || "text"}
                    name={field.name}
                    value={account[field.name]}
                    onChange={(e) => handleChange(e, setAccount, account)}
                    className="w-full border p-2 rounded-md"
                  />
                  {errors[field.name] && (
                    <p className="text-red-600 text-sm">{errors[field.name]}</p>
                  )}
                </div>
              ))}

              <div>
                <label className="font-medium">Business Type</label>
                <select
                  name="businessType"
                  value={account.businessType}
                  onChange={(e) => handleChange(e, setAccount, account)}
                  className="w-full border p-2 rounded-md"
                >
                  <option value="">Select</option>
                  <option>Manufacturer</option>
                  <option>Wholesaler</option>
                  <option>Retailer</option>
                  <option>Trader</option>
                </select>
                {errors.businessType && (
                  <p className="text-red-600 text-sm">{errors.businessType}</p>
                )}
              </div>

              <div className="col-span-full flex justify-end">
                <button onClick={handleNext} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
                  Next →
                </button>
              </div>
            </motion.div>
          )}

          {/* -------------------------------------- */}
          {/* STEP 2 — BUSINESS DETAILS */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -80 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              {[
                { label: "Business Name", name: "businessName" },
                { label: "City", name: "city" },
                { label: "State", name: "state" },
                { label: "Pincode", name: "pincode" },
                { label: "Website", name: "website" },
                { label: "Year Established", name: "yearEstablished" },
                { label: "Employee Count", name: "employeeCount" },
              ].map((field) => (
                <div key={field.name}>
                  <label className="font-medium">{field.label}</label>
                  <input
                    name={field.name}
                    value={business[field.name]}
                    onChange={(e) => handleChange(e, setBusiness, business)}
                    className="w-full border p-2 rounded-md"
                  />
                  {errors[field.name] && (
                    <p className="text-red-600 text-sm">{errors[field.name]}</p>
                  )}
                </div>
              ))}

              {/* PROFILE IMAGE */}
              <div className="col-span-full">
                <label className="font-medium">Profile Image</label>
                <input
                  type="file"
                  onChange={(e) =>
                    handleFile(e, setBusiness, business, "profileImage")
                  }
                  className="w-full border p-2 rounded-md"
                />
              </div>

              <div className="col-span-full flex justify-between">
                <button onClick={handleBack} className="px-6 py-2 bg-gray-400 rounded-lg">
                  ← Back
                </button>
                <button onClick={handleNext} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
                  Next →
                </button>
              </div>
            </motion.div>
          )}

          {/* -------------------------------------- */}
          {/* STEP 3 — PRODUCT DETAILS */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -80 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div>
                <label className="font-medium">Category</label>
                <select
                  name="categories"
                  value={product.categories}
                  onChange={(e) => handleChange(e, setProduct, product)}
                  className="w-full border p-2 rounded-md"
                >
                  <option value="">Select</option>
                  <option>Elevators</option>
                  <option>Motors</option>
                  <option>Cables</option>
                </select>
                {errors.categories && (
                  <p className="text-red-600 text-sm">{errors.categories}</p>
                )}
              </div>

              <div>
                <label className="font-medium">Sub Category</label>
                <input
                  name="subCategories"
                  value={product.subCategories}
                  onChange={(e) => handleChange(e, setProduct, product)}
                  className="w-full border p-2 rounded-md"
                />
                {errors.subCategories && (
                  <p className="text-red-600 text-sm">{errors.subCategories}</p>
                )}
              </div>

              <div className="col-span-full">
                <label className="font-medium">Description</label>
                <textarea
                  name="description"
                  value={product.description}
                  onChange={(e) => handleChange(e, setProduct, product)}
                  className="w-full border p-2 rounded-md"
                  rows="3"
                ></textarea>
                {errors.description && (
                  <p className="text-red-600 text-sm">{errors.description}</p>
                )}
              </div>

              <div className="col-span-full flex justify-between">
                <button onClick={handleBack} className="px-6 py-2 bg-gray-400 rounded-lg">
                  ← Back
                </button>
                <button onClick={handleNext} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
                  Next →
                </button>
              </div>
            </motion.div>
          )}

          {/* -------------------------------------- */}
          {/* STEP 4 — DOCUMENTS */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -80 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              {[
                { label: "GST Number", name: "gstNumber" },
                { label: "PAN Number", name: "panNumber" },
              ].map((field) => (
                <div key={field.name}>
                  <label className="font-medium">{field.label}</label>
                  <input
                    name={field.name}
                    value={gst[field.name]}
                    onChange={(e) => handleChange(e, setGst, gst)}
                    className="w-full border p-2 rounded-md"
                  />
                  {errors[field.name] && (
                    <p className="text-red-600 text-sm">{errors[field.name]}</p>
                  )}
                </div>
              ))}

              {/* FILES */}
              <div className="col-span-full">
                <label className="font-medium">Upload Aadhaar</label>
                <input
                  type="file"
                  onChange={(e) => handleFile(e, setGst, gst, "aadharURL")}
                  className="w-full"
                />
                {errors.aadharURL && (
                  <p className="text-red-600 text-sm">{errors.aadharURL}</p>
                )}
              </div>

              <div className="col-span-full">
                <label className="font-medium">Upload PAN</label>
                <input
                  type="file"
                  onChange={(e) => handleFile(e, setGst, gst, "panurl")}
                  className="w-full"
                />
              </div>

              <div className="col-span-full">
                <label className="font-medium">Upload GST Document</label>
                <input
                  type="file"
                  onChange={(e) => handleFile(e, setGst, gst, "gstDocURL")}
                  className="w-full"
                />
              </div>

              <div className="col-span-full flex justify-between">
                <button onClick={handleBack} className="px-6 py-2 bg-gray-400 rounded-lg">
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg"
                >
                  Submit ✔️
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* RIGHT PREVIEW */}
      <div className="w-full md:w-1/3 bg-gray-50 p-6 rounded-xl shadow-lg h-fit sticky top-0">
        <h3 className="text-xl font-semibold mb-4">Preview</h3>

        <div className="text-sm space-y-4">
          <div>
            <h4 className="font-semibold border-b pb-1 mb-2">Account</h4>
            <p><b>Name:</b> {account.fullName || "—"}</p>
            <p><b>Email:</b> {account.email || "—"}</p>
            <p><b>Phone:</b> {account.phone || "—"}</p>
            <p><b>Type:</b> Seller</p>
          </div>

          <div>
            <h4 className="font-semibold border-b pb-1 mb-2">Business</h4>
            <p><b>Name:</b> {business.businessName || "—"}</p>
            <p><b>City:</b> {business.city || "—"}</p>
            <p><b>Profile Image:</b> {business.profileImage || "—"}</p>
          </div>

          <div>
            <h4 className="font-semibold border-b pb-1 mb-2">GST</h4>
            <p><b>GST:</b> {gst.gstNumber || "—"}</p>
            <p><b>PAN:</b> {gst.panNumber || "—"}</p>
            <p><b>Aadhaar:</b> {gst.aadharURL || "—"}</p>
            <p><b>PAN Doc:</b> {gst.panurl || "—"}</p>
            <p><b>GST Doc:</b> {gst.gstDocURL || "—"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerVerification;
